import { create } from "zustand";
import {
  type Guest,
  Sex,
  type CheckInInfo,
} from "$/api_out/http_checkin/checkin/checkin_type";
import { CheckInService } from "$/api_out/http_checkin/checkin/checkin_srv";
import {
  getStoragedGuestsInfo,
  setStoragedGuestsInfo,
} from "@/utils/getStoragedGuestsInfo";
import {
  type DocumentCodeValue,
  type CountryCodeValue,
  type CareerValue,
} from "@/main";
import { defaultAddressInfo, type AddressInfo } from "@/modal/openAddressModal";
import { addressListToMap } from "@/utils/address";

export enum GuestType {
  Tourist = "1",
  JapanResident = "2",
}

export enum EditStatus {
  init = 0,
  editing = 1,
  done = 2,
}

interface RegistrationStore {
  signedOrderId: string;
  registrationData: CheckInInfo | undefined;
  guestWithStatusList: GuestWithStatus[];
  refresh: (id: string) => Promise<void>;
  updateGuestWithStatusList: (guestWithStatusList: GuestWithStatus[]) => void;
}

export interface GuestWithStatus {
  guest: GuestInfo;
  status: EditStatus;
}

export type DocumentKindValue = DocumentCodeValue | "";

export type GuestInfo = Omit<
  Guest,
  | "nationality"
  | "tel"
  | "document"
  | "residenceCountry"
  | "geoData"
  | "addresses"
  | "postcode"
> & {
  tel: {
    kind: CountryCodeValue | "";
    number: string;
  };
  document: {
    kind: DocumentKindValue;
    number: string;
  };
  profession: CareerValue | "";
  // 地址
  address: AddressInfo;
  nationality: CountryCodeValue | "";
};

export type DocumentInfo = Pick<
  GuestInfo,
  | "nationality"
  | "document"
  | "familyName"
  | "givenName"
  | "birthday"
  | "sex"
  | "documentImgId"
>;

export const getInitData = (): GuestWithStatus => {
  return {
    guest: {
      // 房客id
      id: "",
      // 姓
      familyName: "",
      // 名
      givenName: "",
      // 性别
      sex: Sex.Unknown,
      // 生日
      birthday: "",
      // 职业
      profession: "",
      // 国籍
      nationality: "",
      // 地址相关信息
      address: defaultAddressInfo,
      // 手机号
      tel: {
        kind: "",
        number: "",
      },
      // 邮箱
      email: "",
      // 证件信息
      document: {
        // 统一在选择证件类型步骤判断
        kind: "",
        number: "",
      },
      // 证件图片id
      documentImgId: "",
      preStayAddress: "",
      postStayAddress: "",
    },
    status: EditStatus.init,
  };
};

export const useRegistrationData = create<RegistrationStore>((set, get) => ({
  signedOrderId: "",
  registrationData: undefined,
  guestWithStatusList: [],
  refresh: async (signedOrderId) => {
    const res = await CheckInService.Get({ signedOrderId });
    set({ signedOrderId });
    // 同订单号+状态更新时间一致，使用前端缓存
    const storagedGuestsInfo = getStoragedGuestsInfo(signedOrderId);
    let mergedGuests: GuestWithStatus[] = [];
    if (res.info.lastUpdatedAt === storagedGuestsInfo.updateAt) {
      mergedGuests = storagedGuestsInfo.guestWithStatusList.map((n) => n);
    } else {
      mergedGuests = res.info.guests.map((guest, index) => {
        const {
          nationality,
          residenceCountry,
          geoData,
          addresses,
          tel,
          document,
          profession,
          postcode,
          ...restInfo
        } = guest;
        return {
          guest: {
            address: {
              residenceCountry: residenceCountry as CountryCodeValue,
              geoInfos: geoData.ids.map((id, idx) => ({
                id,
                name: geoData.names[idx] || "",
              })),
              ...addressListToMap(addresses),
              postcode,
            },
            tel: tel as GuestInfo["tel"],
            document: document as GuestInfo["document"],
            profession: profession as CareerValue,
            nationality: nationality as CountryCodeValue,
            ...restInfo,
          },
          status: index === 0 ? EditStatus.editing : EditStatus.init,
        };
      });
      // 本地清掉，防止选择人数时默认选中
      get().updateGuestWithStatusList([]);
    }
    set({
      registrationData: res.info,
      guestWithStatusList: mergedGuests,
    });
  },
  updateGuestWithStatusList: (guestWithStatusList) => {
    set({
      guestWithStatusList,
    });
    setStoragedGuestsInfo(get().signedOrderId, {
      updateAt: get().registrationData?.lastUpdatedAt || "",
      guestWithStatusList,
    });
  },
}));
